import { usePrevious } from "@krea/customer-features/hooks";
import { settings } from "@krea/customer-features/settings";
import Button from "@krea/customer-features/shared-components/button";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@krea/customer-features/shared-components/dialog";
import SsnInput from "@krea/customer-features/shared-components/loan-application-form/fields/ssn";
import { Text } from "@krea/customer-features/shared-components/text";
import { useBankIDStore } from "@krea/customer-features/store/bankID/hooks";
import { useLoanApplicationFormStore } from "@krea/customer-features/store/loan-application-form/hooks";
import { useModalStore } from "@krea/customer-features/store/modal/hooks";
import {
  ssnValidator,
  termsOfServiceLink,
} from "@krea/customer-features/utils";
import { useFormik } from "formik";
import i18n from "i18next";
import { useEffect } from "react";
import { Trans, useTranslation, withTranslation } from "react-i18next";
import * as Yup from "yup";
import { useShallow } from "zustand/react/shallow";

import { useInitiateLoanApplication } from "store/loan-application-form/hooks";

import CancelButton from "./CancelButton";
import { BankIDFlow } from "./RemoteIdentificationBankIDFlow";

interface IState {
  clearFormErrors: boolean;
  formErrors: { errorCode: number; ssn: string; email: string };
  ssn: string;
  setSsn: (ssn: string) => void;
  removeFormError: (field: string) => void;
  clearRemoteIdentification: () => void;
  orderRef: string;
}

const RemoteIdentificationModalBankID = () => {
  const { initiateLoanApplication } = useInitiateLoanApplication();
  const { t } = useTranslation();
  const { isPending, isError, isUserCancel } = useInitiateLoanApplication();

  const { clearFormErrors, errorCode } = useLoanApplicationFormStore(
    (state) => ({
      clearFormErrors: state.clearFormErrors,
      errorCode: state.formErrors.errorCode,
    }),
  );

  const closeModal = useModalStore(
    ({ closeModal }: { closeModal: () => void }) => closeModal,
  );
  const {
    ssn,
    formErrors,
    setSsn,
    removeFormError,
    clearRemoteIdentification,
  } = useLoanApplicationFormStore(
    useShallow((state) => ({
      ssn: state.ssn,
      formErrors: state.formErrors,
      setSsn: state.setSsn,
      removeFormError: state.removeFormError,
      clearRemoteIdentification: state.clearRemoteIdentification,
    })),
  );
  const orderRef = useBankIDStore((state) => state.orderRef);

  const prevFormErrors = usePrevious(formErrors);

  const {
    values,
    errors,
    touched,
    isSubmitting,
    setFieldError,
    setSubmitting,
    handleChange: formikHandleChange,
    handleSubmit,
  } = useFormik({
    initialValues: {
      ssn: ssn || "",
    },
    initialTouched: {
      ssn: !!formErrors.ssn,
    },
    validationSchema: Yup.object().shape({
      ssn: Yup.string()
        .required(t("applicationForm.commons.ssnErrors.required")!)
        .length(12, t("applicationForm.commons.ssnErrors.invalid")!)
        .test("ssn", function (value) {
          const { path, createError } = this;

          return (
            ssnValidator(value, settings.countryCode).isValid ||
            createError({
              path,
              message: t("applicationForm.commons.ssnErrors.invalid")!,
            })
          );
        }),
    }),
    onSubmit: (payload, { setSubmitting }) => {
      setSubmitting(true);
      setSsn(payload.ssn);
      initiateLoanApplication();
    },
    validateOnMount: false,
  });

  const onChange = (e: unknown) => {
    formikHandleChange(e);

    if (formErrors.ssn) {
      removeFormError("ssn");
    } else {
      setFieldError("ssn", formErrors.ssn as string);
    }
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  useEffect(() => {
    if (isError || isUserCancel) {
      setSubmitting(false);
    }
  }, [isError, isUserCancel, setSubmitting]);

  useEffect(() => {
    // Set errors from "outside" (async errors etc).
    if (formErrors.ssn !== prevFormErrors?.ssn) {
      setFieldError("ssn", formErrors.ssn as string);
      setSubmitting(false);
    }

    if (formErrors.email !== prevFormErrors?.email) {
      closeModal();
      clearRemoteIdentification();
      setSsn("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formErrors]);

  return (
    <>
      <DialogTitle
        title={
          isError && errorCode === 1007
            ? t("remoteIdentification.fraudClientErrorPage.title")
            : t("applicationForm.commons.identifyCta")
        }
        closeButton={
          <CancelButton
            isSubmitting={isSubmitting}
            setSubmitting={setSubmitting}
          />
        }
      />

      {orderRef || isError || isUserCancel ? (
        <DialogContent>
          <BankIDFlow isPending={isPending} />
        </DialogContent>
      ) : (
        <>
          <DialogContent>
            <SsnInput
              value={values.ssn}
              errors={errors.ssn}
              touched={touched.ssn}
              onChange={onChange}
              onKeyDown={onKeyDown}
              disabled={isSubmitting}
            />
          </DialogContent>

          <DialogActions>
            <Button
              block
              onClick={() => handleSubmit()}
              disabled={isSubmitting}
              id="modal-submit-button"
              className="tw-mx-auto"
            >
              {t("applicationForm.commons.identifyCustomerBankIDModalButton")}
            </Button>
          </DialogActions>

          <div style={{ overflow: "hidden auto" }}>
            <div className="px-6 pb-5">
              <Text size="sm" className="font-weight-bold text-center">
                {t("applicationForm.commons.subHeading")}
              </Text>

              <Text size="sm" className="text-center font-weight-bold pr-1">
                <Trans i18nKey="applicationForm.commons.tosInfo">
                  Placeholder: Genom att identifiera dig godkänner du&nbsp;
                  <a
                    target="_blank"
                    href={termsOfServiceLink(
                      settings.countryCode,
                      settings.countryWebsiteUrl,
                      i18n.language,
                    )}
                    rel="noopener noreferrer"
                  >
                    Placeholder: användarvillkoren
                  </a>
                </Trans>
              </Text>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default withTranslation()(RemoteIdentificationModalBankID);
